/**
 * AdminContribucionesV2 - Contributions management for administrators.
 * Design from Lovable, logic from chirigota.
 */
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
	Building2,
	Users,
	CalendarClock,
	Wallet,
	TrendingUp,
	CheckCircle2,
	Clock,
	AlertCircle,
	ChevronRight,
	Gift,
	Download,
	Eye,
	EyeOff
} from 'lucide-react';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer
} from 'recharts';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { PageHeader } from '../../../components/ui/page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '../../../components/ui/table';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '../../../components/ui/select';
import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import { formatArcaNumber } from '../../../utils/formatArcaNumbers';
import getAdminMonthlyContributionsByYear from '../../../api/calculators/getAdminMonthlyContributionsByYear';
import findParticipantsContributionData, {
	UserOnboardingStatusFilter
} from '../../../api/calculators/findParticipantsContributionData';
import getCalculatorsAdminAportations from '../../../api/calculators/getCalculatorsAdminAportations';
import { getAllCompanyBonuses } from '../../../api/bonus/getBonusConfig';
import getCompanyInfoEppConfig from '../../../api/companies/getCompanyInfoEppConfig';
import { getNextContributionDate } from '../../../utils/dateUtils';
import { classifyContribution } from '../../../utils/contributionClassification';
import {
	AdminContributionStatusEnum,
	isCurrentOrFutureMonth,
	resolveAdminMonthStatus,
	resolveAdminMovementStatus
} from '../../../utils/adminContributionStatus';
import {
	ContributionFromEnum,
	ContributionStatusEnum
} from '../../../api/users/interfaces/ContributionData';

interface ChartDataPoint {
	month: string;
	empresa: number;
	empleados: number;
}

interface MonthContribution {
	mes: string;
	empresaTotal: number;
	empleadoTotal: number;
	total: number;
	estado: AdminContributionStatusEnum;
	empleadosContribuyentes: number;
	movimientos: ContributionMovement[];
}

interface ContributionMovement {
	empleado: string;
	origen: 'empresa' | 'empleado';
	importe: number;
	estado: AdminContributionStatusEnum;
	precioParticipacion: number | null;
	participaciones: number | null;
	fechaValor: string;
}

interface BonusRow {
	id: string;
	employeeName: string;
	importe: number;
	motivo: string;
	deadline: string;
	estado: 'confirmed' | 'pending' | 'decided' | 'expired' | 'executed';
	employeeDecisionAmount: number | null;
}

const STATUS_CONFIG = {
	confirmed: {
		labelKey: 'admin.contributions.status.confirmed',
		icon: CheckCircle2,
		className: 'bg-primary/10 text-primary border-primary/20'
	},
	pending: {
		labelKey: 'admin.contributions.status.pending',
		icon: Clock,
		className: 'bg-amber-500/10 text-amber-600 border-amber-500/20'
	},
	decided: {
		labelKey: 'admin.contributions.status.decided',
		icon: CheckCircle2,
		className: 'bg-primary/10 text-primary border-primary/20'
	},
	expired: {
		labelKey: 'admin.contributions.status.expired',
		icon: AlertCircle,
		className: 'bg-muted text-muted-foreground border-muted'
	},
	executed: {
		labelKey: 'admin.contributions.status.executed',
		icon: CheckCircle2,
		className: 'bg-primary/10 text-primary border-primary/20'
	}
};

const fadeUp = {
	hidden: { opacity: 0, y: 16 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.08, duration: 0.4, ease: [0, 0, 0.58, 1] as const }
	})
};

const PAGE_SIZE = 5;

const AdminContribucionesV2: React.FC = () => {
	const { token, companyId } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);

	const MONTH_NAMES = useMemo(
		() => Array.from({ length: 12 }, (_, i) => translate(`admin.contributions.months.${i}`)),
		[translate]
	);
	const MONTH_LABELS = useMemo(
		() => Array.from({ length: 12 }, (_, i) => translate(`admin.contributions.monthsShort.${i}`)),
		[translate]
	);

	const [isLoading, setIsLoading] = useState(true);
	const [showAccount, setShowAccount] = useState(false);
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
	const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
	const [currentPage, setCurrentPage] = useState(1);
	const [filterQuarter, setFilterQuarter] = useState<string>('all');

	// Data states
	const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
	const [monthContributions, setMonthContributions] = useState<MonthContribution[]>([]);
	const [bonusList, setBonusList] = useState<BonusRow[]>([]);
	const [nextContribution, setNextContribution] = useState(0);
	const [nextContribDate, setNextContribDate] = useState('');
	const [companyAccounts, setCompanyAccounts] = useState<{ name: string; iban: string }[]>([]);
	const [contributionDay, setContributionDay] = useState(20);

	const availableYears = useMemo(() => {
		const currentYear = new Date().getFullYear();
		return [currentYear, currentYear - 1].filter((y) => y >= 2024);
	}, []);

	const fetchData = useCallback(async () => {
		if (!token || !companyId) return;
		setIsLoading(true);

		try {
			const [
				monthlyRes,
				participantsRes,
				inactiveParticipantsRes,
				aportationsRes,
				bonusesRes,
				configRes
			] = await Promise.all([
				getAdminMonthlyContributionsByYear(token, selectedYear),
				// SIN companyId (fix bug multi-SL): así el backend aplica el MISMO scope que
				// all-aportations-by-type y admin-monthly-contributions-by-year. Para un admin con
				// membership de holding devuelve empleados de TODAS las SLs → los nombres cuadran con
				// los movimientos (antes solo se resolvían los de la SL activa y el resto caía a
				// "Empleado"). Para un admin legacy el backend cae a adminUser.companyId → sin cambio.
				findParticipantsContributionData(token, 1, 500, selectedYear, null, null, null),
				// 2ª llamada: empleados de baja (TERMINATED). Solo para resolver sus nombres en el
				// userNameMap (el default null excluye inactivos). NO aporta a totales ni participants[].
				// También sin companyId, por coherencia de scope con la 1ª llamada (bajas de cualquier SL).
				// No-fatal: si falla, degrada a "Empleado" en vez de tumbar toda la pantalla
				// (es una mejora cosmética, no debe acoplar la carga de la página crítica de contribuciones).
				findParticipantsContributionData(
					token,
					1,
					500,
					selectedYear,
					UserOnboardingStatusFilter.NOT_ACTIVE_IN_ARCA_OR_EMPLOYMENT_STATUS_NOT_CURRENTLY_EMPLOYED,
					null,
					null
				).catch(() => ({ data: { items: [] } })),
				getCalculatorsAdminAportations(
					token,
					1,
					1000,
					undefined,
					undefined,
					undefined,
					selectedYear
				),
				getAllCompanyBonuses(token, companyId, 1, 100),
				getCompanyInfoEppConfig(token, companyId)
			]);

			// Build participants list from participants-contributions data (for summary cards)
			const participants: Array<{ name: string; empresa: number; empleado: number }> = [];
			interface ParticipantItem {
				user?: { id?: string; name?: string; email?: string };
				nextPromoterContribution?: number;
				nextParticipantContribution?: number;
			}
			const userNameMap = new Map<string, string>();
			if (participantsRes?.data?.items) {
				participantsRes.data.items.forEach((item: ParticipantItem) => {
					const user = item.user || {};
					const name = user.name || user.email || 'Empleado';
					if (user.id) userNameMap.set(user.id, name);
					participants.push({
						name,
						empresa: item.nextPromoterContribution || 0,
						empleado: item.nextParticipantContribution || 0
					});
				});
			}

			// Merge SOLO de nombres de empleados de baja (TERMINATED): así las líneas históricas de
			// inactivos muestran su nombre en vez de "Empleado". NO se añaden a participants[] ni a
			// totales/próxima aportación (su aportación ya cesó).
			if (inactiveParticipantsRes?.data?.items) {
				inactiveParticipantsRes.data.items.forEach((item: ParticipantItem) => {
					const user = item.user || {};
					const name = user.name || user.email;
					if (user.id && name) userNameMap.set(user.id, name);
				});
			}
			const totalParticipants = participants.length;
			const nextTotal = participants.reduce((sum, p) => sum + p.empresa + p.empleado, 0);
			setNextContribution(nextTotal);

			// Build historical contributions grouped by month (0-indexed)
			interface AportationItem {
				userId: string;
				contributionAmount: number;
				typeContribution: string;
				dateContribution: string;
				statusContribution?: ContributionStatusEnum;
				liquidationValue?: number;
				sharesBought?: number;
			}
			const contributionsByMonth = new Map<number, AportationItem[]>();
			if (aportationsRes?.data?.items) {
				aportationsRes.data.items.forEach((c: AportationItem) => {
					const monthIdx = new Date(c.dateContribution).getMonth();
					if (!contributionsByMonth.has(monthIdx)) contributionsByMonth.set(monthIdx, []);
					contributionsByMonth.get(monthIdx)!.push(c);
				});
			}

			// Extract contributionDay from config early — used in fechaValor and next date calc
			const day = configRes?.data?.allCompanies?.[0]?.companyEppConfig?.contributionDay || 20;
			setContributionDay(day);

			// Process chart data from arrays format
			if (monthlyRes.success && monthlyRes.data) {
				const { aportacionesNetasEmpresa, aportacionesNetasEmpleados, totalMonths } =
					monthlyRes.data;
				const months = totalMonths || 12;
				const formattedChart = MONTH_LABELS.slice(0, months).map((month, index) => ({
					month,
					empresa: aportacionesNetasEmpresa[index] || 0,
					empleados: aportacionesNetasEmpleados[index] || 0
				}));
				setChartData(formattedChart);

				// Create month contributions from arrays
				const now = new Date();
				const monthContribs: MonthContribution[] = MONTH_LABELS.slice(0, months)
					.map((_, index) => {
						const monthNum = index + 1;
						const empresaVal = aportacionesNetasEmpresa[index] || 0;
						const empleadosVal = aportacionesNetasEmpleados[index] || 0;
						const hasActivity = empresaVal > 0 || empleadosVal > 0;

						// Build movimientos per month from real historical data
						// Exclude Caser internal rebalancing/noise types (TRANSFER_INTERNAL_*, etc.) — see contributionClassification.ts
						const monthAportations = (contributionsByMonth.get(index) || []).filter(
							(c) => classifyContribution(c.typeContribution as ContributionFromEnum) !== null
						);
						const movimientos: ContributionMovement[] = monthAportations.map((c) => ({
							empleado: userNameMap.get(c.userId) || 'Empleado',
							origen: (c.typeContribution === 'COMPANY' ? 'empresa' : 'empleado') as
								| 'empresa'
								| 'empleado',
							importe: c.contributionAmount,
							estado: resolveAdminMovementStatus(c.statusContribution),
							precioParticipacion: c.liquidationValue || null,
							participaciones: c.sharesBought || null,
							fechaValor: c.dateContribution
						}));

						return {
							mes: `${selectedYear}-${String(monthNum).padStart(2, '0')}`,
							empresaTotal: empresaVal,
							empleadoTotal: empleadosVal,
							total: empresaVal + empleadosVal,
							estado: resolveAdminMonthStatus(
								movimientos.map((m) => m.estado),
								isCurrentOrFutureMonth(selectedYear, monthNum, now),
								hasActivity
							),
							empleadosContribuyentes: hasActivity ? totalParticipants : 0,
							movimientos
						};
					})
					.reverse();
				setMonthContributions(monthContribs);
			}

			// Process bonuses
			if (bonusesRes.success && bonusesRes.data?.items) {
				const formattedBonuses: BonusRow[] = bonusesRes.data.items.map((item) => ({
					id: item.bonus.id,
					employeeName: item.userName || item.userEmail || 'Empleado',
					importe: item.bonus.amount || 0,
					motivo: '',
					deadline: item.bonus.decisionDeadline || '',
					estado: (item.bonus.status?.toLowerCase() || 'pending') as BonusRow['estado'],
					employeeDecisionAmount: item.bonus.decisionAmount ?? null
				}));
				setBonusList(formattedBonuses);
			}

			// Process bank accounts for all companies
			if (configRes.success && configRes.data) {
				const accounts = (configRes.data.allCompanies || [])
					.map((c) => ({
						name: c.name || '',
						iban: c.bankAccount || c.saasBankAccount || ''
					}))
					.filter((a) => a.iban);
				setCompanyAccounts(accounts);
			}

			// Calculate next contribution date
			const nextDate = getNextContributionDate(day);
			setNextContribDate(
				nextDate.toLocaleDateString('es-ES', {
					day: 'numeric',
					month: 'long',
					year: 'numeric'
				})
			);

			// Sum of latest month as next contribution - done inside monthlyRes processing above
		} catch (error) {
			console.error('Error fetching contributions data:', error);
		} finally {
			setIsLoading(false);
		}
	}, [token, companyId, selectedYear, MONTH_LABELS]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Totals
	const yearTotals = useMemo(() => {
		const empresa = chartData.reduce((s, d) => s + d.empresa, 0);
		const empleados = chartData.reduce((s, d) => s + d.empleados, 0);
		return { empresa, empleados, total: empresa + empleados };
	}, [chartData]);

	// Filter by quarter
	const filteredData = useMemo(() => {
		if (filterQuarter === 'all') return monthContributions;
		const q = parseInt(filterQuarter);
		const startMonth = (q - 1) * 3 + 1;
		const endMonth = q * 3;
		return monthContributions.filter((g) => {
			const m = parseInt(g.mes.split('-')[1]);
			return m >= startMonth && m <= endMonth;
		});
	}, [monthContributions, filterQuarter]);

	// Pagination
	const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
	const paginatedData = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

	const toggleMonth = (mes: string) => {
		setExpandedMonths((prev) => {
			const next = new Set(prev);
			if (next.has(mes)) next.delete(mes);
			else next.add(mes);
			return next;
		});
	};

	const formatMonth = (mes: string) => {
		const [y, m] = mes.split('-');
		return `${MONTH_NAMES[parseInt(m) - 1]} ${y}`;
	};

	// Days to next contribution
	const nextDateCalc = getNextContributionDate(contributionDay);
	const daysToNext = Math.ceil((nextDateCalc.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
			</div>
		);
	}

	return (
		<motion.div initial="hidden" animate="visible" className="space-y-6">
			{/* Cabecera — la pone `ui/PageHeader`, puerto de Proa v0.10.0. Antes era un `h1` a
			    `text-2xl font-bold`: sin `font-heading` y dos tallas por debajo del canon.
			    `width="full"` porque esta pantalla NO tenía contenedor y va a todo el ancho;
			    darle el ancho por defecto la estrecharía, y estrechar tablas no es lo que
			    veníamos a arreglar (decisión de Marisa, 29/09/26). */}
			<motion.div custom={0} variants={fadeUp}>
				<PageHeader
					width="full"
					title={translate('admin.contributions.title')}
					subtitle={translate('admin.contributions.description')}
				/>
			</motion.div>

			{/* Summary Cards */}
			<motion.div
				custom={1}
				variants={fadeUp}
				className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
			>
				{/* Next contribution */}
				<Card className="border-none shadow-xs">
					<CardContent className="p-5">
						<div className="flex items-center gap-2 mb-3">
							<div className="w-9 h-9 rounded-lg bg-secondary/10 flex items-center justify-center">
								<CalendarClock className="w-4 h-4 text-secondary" />
							</div>
							<p className="text-sm text-muted-foreground">
								{translate('admin.contributions.nextContributionTitle')}
							</p>
						</div>
						<p className="text-3xl font-bold text-foreground">
							{formatArcaNumber(nextContribution)} €
						</p>
						<div className="mt-3 space-y-1.5">
							<div className="flex items-center gap-2">
								<Progress value={((30 - daysToNext) / 30) * 100} className="h-1.5 flex-1" />
								<span className="text-xs font-medium text-secondary">
									{daysToNext} {translate('admin.contributions.days')}
								</span>
							</div>
							<p className="text-xs text-muted-foreground">
								{translate('admin.contributions.deadline')}:{' '}
								<span className="font-medium text-foreground">{nextContribDate}</span>
							</p>
						</div>
					</CardContent>
				</Card>

				{/* Total empresa */}
				<Card className="border-none shadow-xs">
					<CardContent className="p-5">
						<div className="flex items-center gap-2 mb-3">
							<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
								<Building2 className="w-4 h-4 text-primary" />
							</div>
							<p className="text-sm text-muted-foreground">
								{translate('admin.contributions.totalCompany')} {selectedYear}
							</p>
						</div>
						<p className="text-3xl font-bold text-foreground">
							{formatArcaNumber(yearTotals.empresa)} €
						</p>
						<p className="text-xs text-muted-foreground mt-2">
							{translate('admin.contributions.monthlyAverage')}:{' '}
							<span className="font-medium text-foreground">
								{formatArcaNumber(yearTotals.empresa / Math.max(chartData.length, 1))} €
							</span>
						</p>
					</CardContent>
				</Card>

				{/* Total empleados */}
				<Card className="border-none shadow-xs">
					<CardContent className="p-5">
						<div className="flex items-center gap-2 mb-3">
							<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
								<Users className="w-4 h-4 text-primary" />
							</div>
							<p className="text-sm text-muted-foreground">
								{translate('admin.contributions.totalEmployees')} {selectedYear}
							</p>
						</div>
						<p className="text-3xl font-bold text-foreground">
							{formatArcaNumber(yearTotals.empleados)} €
						</p>
						<p className="text-xs text-muted-foreground mt-2">
							{translate('admin.contributions.participation')}:{' '}
							<span className="font-medium text-primary">
								{chartData.length > 0
									? Math.round((yearTotals.empleados / (yearTotals.total || 1)) * 100)
									: 0}
								%
							</span>{' '}
							{translate('admin.contributions.ofTotal')}
						</p>
					</CardContent>
				</Card>

				{/* Cuentas de cobro */}
				{companyAccounts.map((acct, idx) => (
					<Card key={idx} className="border-none shadow-xs">
						<CardContent className="p-5">
							<div className="flex items-center justify-between mb-3">
								<div className="flex items-center gap-2">
									<div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
										<Wallet className="w-4 h-4 text-muted-foreground" />
									</div>
									<p className="text-sm text-muted-foreground">
										{translate('admin.contributions.collectionAccount')}
									</p>
								</div>
								<button
									onClick={() => setShowAccount(!showAccount)}
									className="text-muted-foreground hover:text-foreground transition-colors"
								>
									{showAccount ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
								</button>
							</div>
							<p className="text-xs text-muted-foreground mb-1">{acct.name} · Cecabank</p>
							<p className="text-sm font-mono text-foreground">
								{showAccount
									? acct.iban.replace(/(.{4})/g, '$1 ').trim()
									: `•••• •••• •••• •••• •••• ${acct.iban.slice(-4)}`}
							</p>
							<div className="flex items-center gap-1 mt-2">
								<CheckCircle2 className="w-3.5 h-3.5 text-primary" />
								<p className="text-xs text-primary font-medium">
									{translate('admin.contributions.sepaActive')}
								</p>
							</div>
						</CardContent>
					</Card>
				))}
			</motion.div>

			{/* Tabs */}
			<Tabs defaultValue="contribuciones" className="space-y-6">
				{/* 🔴 `flex-wrap` + `h-auto`: «Bonus Extraordinarios» se salía 1,2 px y llevaba el
					    documento a 395 px. Mismo caso que el simulador de rescate, que ya se arregló
					    así: una tira de pestañas que no puede envolver desborda en cuanto el texto
					    crece — y creció al pasar a Inter. `w-fit` se queda: la tira no debe ocupar
					    todo el ancho cuando sí cabe. */}
				<TabsList className="bg-muted/50 p-1 rounded-full w-fit flex-wrap h-auto">
					<TabsTrigger
						value="contribuciones"
						className="rounded-full px-6 data-[state=active]:bg-card data-[state=active]:shadow-xs"
					>
						{translate('admin.contributions.tabContributions')}
					</TabsTrigger>
					<TabsTrigger
						value="bonus"
						className="rounded-full px-6 data-[state=active]:bg-card data-[state=active]:shadow-xs"
					>
						<Gift className="w-4 h-4 mr-1.5" />
						{translate('admin.contributions.bonuses.title')}
						{bonusList.filter((b) => b.estado === 'pending').length > 0 && (
							<Badge className="ml-1.5 h-5 px-1.5 text-xs bg-amber-500 text-white">
								{bonusList.filter((b) => b.estado === 'pending').length}
							</Badge>
						)}
					</TabsTrigger>
				</TabsList>

				{/* Contributions Tab */}
				<TabsContent value="contribuciones" className="space-y-6">
					{/* Chart */}
					<motion.div custom={2} variants={fadeUp}>
						<Card className="border-none shadow-xs">
							<CardContent className="p-4 sm:p-6">
								<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
									<div className="flex items-center gap-2">
										<TrendingUp className="w-5 h-5 text-primary" />
										<h2 className="text-lg font-semibold text-foreground">
											{translate('admin.contributions.evolutionTitle')}
										</h2>
									</div>
									<Select
										value={String(selectedYear)}
										onValueChange={(v) => {
											setSelectedYear(Number(v));
											setCurrentPage(1);
											setExpandedMonths(new Set());
											setFilterQuarter('all');
										}}
									>
										<SelectTrigger className="w-[120px]">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{availableYears.map((year) => (
												<SelectItem key={year} value={String(year)}>
													{year}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								{/* Totals */}
								<div className="grid grid-cols-2 gap-4 mb-6">
									<div className="bg-primary/5 rounded-lg p-3 text-center">
										<p className="text-xs text-muted-foreground mb-1">
											{translate('admin.contributions.totalCompany')} {selectedYear}
										</p>
										<p className="text-lg font-bold text-primary">
											{formatArcaNumber(yearTotals.empresa)} €
										</p>
									</div>
									<div className="bg-secondary/5 rounded-lg p-3 text-center">
										<p className="text-xs text-muted-foreground mb-1">
											{translate('admin.contributions.totalEmployees')} {selectedYear}
										</p>
										<p className="text-lg font-bold text-secondary">
											{formatArcaNumber(yearTotals.empleados)} €
										</p>
									</div>
								</div>

								{/* Chart */}
								<div className="h-[300px] w-full">
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
											<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
											<XAxis
												dataKey="month"
												tick={{ fontSize: 11, fill: '#9ca3af' }}
												axisLine={false}
												tickLine={false}
											/>
											<YAxis
												tick={{ fontSize: 11, fill: '#9ca3af' }}
												tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
												axisLine={false}
												tickLine={false}
											/>
											<Tooltip
												contentStyle={{
													backgroundColor: 'white',
													border: '1px solid #e5e7eb',
													borderRadius: '8px',
													fontSize: '12px'
												}}
												formatter={(value) => [`${formatArcaNumber(value as number)} €`]}
											/>
											<Legend wrapperStyle={{ fontSize: '11px' }} />
											<Bar
												dataKey="empresa"
												name={translate('admin.contributions.company')}
												fill="#0D8044"
												radius={[4, 4, 0, 0]}
											/>
											<Bar
												dataKey="empleados"
												name={translate('admin.contributions.employees')}
												fill="#F73469"
												radius={[4, 4, 0, 0]}
											/>
										</BarChart>
									</ResponsiveContainer>
								</div>
							</CardContent>
						</Card>
					</motion.div>

					{/* Filters & Export */}
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div className="flex items-center gap-3">
							<Select
								value={filterQuarter}
								onValueChange={(v) => {
									setFilterQuarter(v);
									setCurrentPage(1);
								}}
							>
								<SelectTrigger className="w-[180px] h-9 text-sm">
									<SelectValue placeholder={translate('admin.contributions.filterByQuarter')} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{translate('admin.contributions.allMonths')}</SelectItem>
									<SelectItem value="1">{translate('admin.contributions.q1')}</SelectItem>
									<SelectItem value="2">{translate('admin.contributions.q2')}</SelectItem>
									<SelectItem value="3">{translate('admin.contributions.q3')}</SelectItem>
									<SelectItem value="4">{translate('admin.contributions.q4')}</SelectItem>
								</SelectContent>
							</Select>

							<p className="text-xs text-muted-foreground">
								{filteredData.length}{' '}
								{filteredData.length === 1
									? translate('admin.contributions.monthSingular')
									: translate('admin.contributions.monthPlural')}{' '}
								· Total:{' '}
								<span className="font-medium text-foreground">
									{formatArcaNumber(filteredData.reduce((s, g) => s + g.total, 0))} €
								</span>
							</p>
						</div>

						<Button variant="outline" size="sm" className="gap-1.5 text-xs">
							<Download className="w-3.5 h-3.5" />
							{translate('admin.contributions.exportCsv')}
						</Button>
					</div>

					{/* History Table */}
					<Card className="border-none shadow-xs overflow-hidden">
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<TableHead className="w-8" />
										<TableHead>{translate('admin.contributions.tableMonth')}</TableHead>
										<TableHead className="text-right">
											{translate('admin.contributions.tableCompany')}
										</TableHead>
										<TableHead className="text-right">
											{translate('admin.contributions.tableEmployees')}
										</TableHead>
										<TableHead className="text-right">
											{translate('admin.contributions.tableTotal')}
										</TableHead>
										<TableHead className="text-center">
											{translate('admin.contributions.tableParticipants')}
										</TableHead>
										<TableHead className="text-center">
											{translate('admin.contributions.tableStatus')}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{paginatedData.length === 0 ? (
										<TableRow>
											<TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
												{translate('admin.contributions.noContributions')}
											</TableCell>
										</TableRow>
									) : (
										paginatedData.map((group) => {
											const isExpanded = expandedMonths.has(group.mes);
											const StatusIcon = STATUS_CONFIG[group.estado].icon;

											return (
												<React.Fragment key={group.mes}>
													<TableRow
														className="cursor-pointer hover:bg-muted/40 transition-colors"
														onClick={() => toggleMonth(group.mes)}
													>
														<TableCell className="w-8 px-3">
															<ChevronRight
																className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
															/>
														</TableCell>
														<TableCell className="font-medium">{formatMonth(group.mes)}</TableCell>
														<TableCell className="text-right">
															<span className="text-primary font-medium">
																{formatArcaNumber(group.empresaTotal)} €
															</span>
														</TableCell>
														<TableCell className="text-right">
															<span className="text-secondary font-medium">
																{formatArcaNumber(group.empleadoTotal)} €
															</span>
														</TableCell>
														<TableCell className="text-right font-semibold">
															{formatArcaNumber(group.total)} €
														</TableCell>
														<TableCell className="text-center">
															<Badge variant="outline" className="text-xs font-medium">
																<Users className="w-3 h-3 mr-1" />
																{group.empleadosContribuyentes}
															</Badge>
														</TableCell>
														<TableCell className="text-center">
															<Badge
																variant="outline"
																className={`text-xs gap-1 ${STATUS_CONFIG[group.estado].className}`}
															>
																<StatusIcon className="w-3 h-3" />
																{translate(STATUS_CONFIG[group.estado].labelKey)}
															</Badge>
														</TableCell>
													</TableRow>

													{isExpanded &&
														group.movimientos.map((mov, i) => (
															<TableRow key={`${group.mes}-${i}`} className="bg-muted/20">
																<TableCell />
																<TableCell className="pl-8">
																	<div className="flex items-center gap-2">
																		{mov.origen === 'empresa' ? (
																			<Building2 className="w-3.5 h-3.5 text-primary" />
																		) : (
																			<Users className="w-3.5 h-3.5 text-secondary" />
																		)}
																		<span className="text-sm">{mov.empleado}</span>
																	</div>
																</TableCell>
																<TableCell className="text-right text-sm">
																	{mov.origen === 'empresa'
																		? `${formatArcaNumber(mov.importe)} €`
																		: '—'}
																</TableCell>
																<TableCell className="text-right text-sm">
																	{mov.origen === 'empleado'
																		? `${formatArcaNumber(mov.importe)} €`
																		: '—'}
																</TableCell>
																<TableCell className="text-right text-sm font-medium">
																	{formatArcaNumber(mov.importe)} €
																</TableCell>
																<TableCell className="text-center text-xs text-muted-foreground">
																	{mov.precioParticipacion
																		? `${mov.precioParticipacion.toFixed(4)} € · ${mov.participaciones} part.`
																		: translate('admin.contributions.pendingValue')}
																</TableCell>
																<TableCell className="text-center">
																	<Badge
																		variant="outline"
																		className={`text-xs gap-1 ${STATUS_CONFIG[mov.estado].className}`}
																	>
																		{translate(STATUS_CONFIG[mov.estado].labelKey)}
																	</Badge>
																</TableCell>
															</TableRow>
														))}
												</React.Fragment>
											);
										})
									)}
								</TableBody>
							</Table>
						</div>

						{/* Pagination */}
						{totalPages > 1 && (
							<div className="flex items-center justify-between px-4 py-3 border-t border-border">
								<p className="text-xs text-muted-foreground">
									{translate('admin.contributions.page')} {currentPage}{' '}
									{translate('admin.contributions.of')} {totalPages}
								</p>
								<div className="flex gap-1">
									<Button
										variant="outline"
										size="sm"
										disabled={currentPage === 1}
										onClick={() => setCurrentPage((p) => p - 1)}
										className="h-8 px-3 text-xs"
									>
										{translate('general.previous')}
									</Button>
									{Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
										<Button
											key={page}
											variant={page === currentPage ? 'default' : 'outline'}
											size="sm"
											onClick={() => setCurrentPage(page)}
											className="h-8 w-8 p-0 text-xs"
										>
											{page}
										</Button>
									))}
									<Button
										variant="outline"
										size="sm"
										disabled={currentPage === totalPages}
										onClick={() => setCurrentPage((p) => p + 1)}
										className="h-8 px-3 text-xs"
									>
										{translate('general.next')}
									</Button>
								</div>
							</div>
						)}
					</Card>

					{/* Annual summary */}
					<Card className="border-none shadow-xs bg-linear-to-br from-primary/5 to-transparent">
						<CardContent className="p-5">
							<div className="flex items-center gap-2 mb-3">
								<TrendingUp className="w-4 h-4 text-primary" />
								<h3 className="text-sm font-semibold">
									{translate('admin.contributions.annualSummary')} {selectedYear}
								</h3>
							</div>
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
								<div>
									<p className="text-xs text-muted-foreground">
										{translate('admin.contributions.totalContributed')}
									</p>
									<p className="text-lg font-bold text-foreground">
										{formatArcaNumber(yearTotals.total)} €
									</p>
								</div>
								<div>
									<p className="text-xs text-muted-foreground">
										{translate('admin.contributions.company')}
									</p>
									<p className="text-lg font-bold text-primary">
										{formatArcaNumber(yearTotals.empresa)} €
									</p>
								</div>
								<div>
									<p className="text-xs text-muted-foreground">
										{translate('admin.contributions.employees')}
									</p>
									<p className="text-lg font-bold text-secondary">
										{formatArcaNumber(yearTotals.empleados)} €
									</p>
								</div>
								<div>
									<p className="text-xs text-muted-foreground">
										{translate('admin.contributions.monthlyAverageTotal')}
									</p>
									<p className="text-lg font-bold text-foreground">
										{formatArcaNumber(yearTotals.total / Math.max(chartData.length, 1))} €
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</TabsContent>

				{/* Bonus Tab */}
				<TabsContent value="bonus" className="space-y-6">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-lg font-semibold">
								{translate('admin.contributions.bonuses.title')}
							</h2>
							<p className="text-sm text-muted-foreground">
								{translate('admin.contributions.bonusesDescription')}
							</p>
						</div>
						<Button className="gap-1.5">
							<Gift className="w-4 h-4" />
							{translate('admin.contributions.bonuses.createBonusButton')}
						</Button>
					</div>

					<Card className="border-none shadow-xs overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="hover:bg-transparent">
									<TableHead>{translate('admin.contributions.bonuses.employee')}</TableHead>
									<TableHead>{translate('admin.contributions.bonuses.reason')}</TableHead>
									<TableHead className="text-right">
										{translate('admin.contributions.bonuses.amount')}
									</TableHead>
									<TableHead>{translate('admin.contributions.bonuses.deadline')}</TableHead>
									<TableHead className="text-right">
										{translate('admin.contributions.bonuses.decision')}
									</TableHead>
									<TableHead className="text-center">
										{translate('admin.contributions.bonuses.status.title')}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{bonusList.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} className="text-center py-12">
											<div className="flex flex-col items-center gap-2 text-muted-foreground">
												<Gift className="w-8 h-8 opacity-40" />
												<p className="text-sm font-medium">
													{translate('admin.contributions.bonuses.noBonuses')}
												</p>
												<p className="text-xs">
													{translate('admin.contributions.bonuses.noBonusesDescription')}
												</p>
											</div>
										</TableCell>
									</TableRow>
								) : (
									bonusList.map((bonus) => {
										const statusKey = bonus.estado as keyof typeof STATUS_CONFIG;
										const StatusIcon = STATUS_CONFIG[statusKey]?.icon || CheckCircle2;
										return (
											<TableRow key={bonus.id}>
												<TableCell className="font-medium">{bonus.employeeName}</TableCell>
												<TableCell className="text-muted-foreground">{bonus.motivo}</TableCell>
												<TableCell className="text-right font-semibold">
													{formatArcaNumber(bonus.importe)} €
												</TableCell>
												<TableCell className="text-muted-foreground">
													{bonus.deadline
														? new Date(bonus.deadline).toLocaleDateString('es-ES')
														: '—'}
												</TableCell>
												<TableCell className="text-right">
													{bonus.employeeDecisionAmount != null ? (
														<span className="font-medium text-primary">
															{formatArcaNumber(bonus.employeeDecisionAmount)} €
														</span>
													) : (
														<span className="text-muted-foreground text-xs">
															{translate('admin.contributions.bonuses.status.pending')}
														</span>
													)}
												</TableCell>
												<TableCell className="text-center">
													<Badge
														variant="outline"
														className={`text-xs gap-1 ${STATUS_CONFIG[statusKey]?.className || ''}`}
													>
														<StatusIcon className="w-3 h-3" />
														{translate(`admin.contributions.bonuses.status.${statusKey}`)}
													</Badge>
												</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					</Card>

					{/* Bonus summary */}
					<Card className="border-none shadow-xs">
						<CardContent className="p-5">
							<div className="grid grid-cols-3 gap-4 text-center">
								<div>
									<p className="text-xs text-muted-foreground">
										{translate('admin.contributions.bonuses.totalBonus')}
									</p>
									<p className="text-xl font-bold text-foreground">
										{formatArcaNumber(bonusList.reduce((s, b) => s + b.importe, 0))} €
									</p>
								</div>
								<div>
									<p className="text-xs text-muted-foreground">
										{translate('admin.contributions.bonuses.decided')}
									</p>
									<p className="text-xl font-bold text-primary">
										{
											bonusList.filter(
												(b) =>
													b.estado === 'decided' ||
													b.estado === 'confirmed' ||
													b.estado === 'executed'
											).length
										}
									</p>
								</div>
								<div>
									<p className="text-xs text-muted-foreground">
										{translate('admin.contributions.bonuses.pendingCount')}
									</p>
									<p className="text-xl font-bold text-secondary">
										{bonusList.filter((b) => b.estado === 'pending').length}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>
		</motion.div>
	);
};

export default AdminContribucionesV2;
